import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Flame, Clock, Search, ArrowLeft } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { BillCard } from "@/components/bills/BillTracker";
import { BillSources } from "@/components/bills/BillSources";
import { Footer } from "@/components/Footer";
import { WriteLetterCta } from "@/components/letter/WriteLetterCta";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const STATUSES = ["watching", "passed", "defeated", "withdrawn", "lapsed"];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#1B2A41] border-[#1B2A41] text-[#FDFBF7]" : "border-[#D8D2C4] text-[#1B2A41] hover:border-[#8FA58A]"}`}>
    {children}
  </button>
);

export default function BillBoard() {
  const { member } = useMember();
  const [f, setF] = useState({ state: "", type: "", status: "", sort: "hot", q: "" });
  const { data, isLoading } = useQuery({
    queryKey: ["bill-board", f, member?.id],
    queryFn: () => api.get("/bills/board", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => r.data),
  });
  const bills = data?.bills || [];
  const sel = "h-8 rounded-full border border-[#D8D2C4] bg-[#FAF8F2] px-3 text-xs font-semibold text-[#1B2A41]";

  return (
    <div className="min-h-screen bg-[#F3EFE4]" data-testid="bill-board-page">
      <header className="bg-[#121E2B] text-[#FDFBF7] relative grain overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" data-testid="bill-board-back-link" className="inline-flex items-center gap-1.5 text-xs font-type uppercase tracking-widest text-[#8FA58A] hover:text-[#E2705A]"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
            <Link to="/"><img src={LOGO_URL} alt="R.O.A.N" className="h-12 object-contain" /></Link>
          </div>
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#8FA58A]">Every electorate · one board</p>
          <h1 className="font-type font-bold text-4xl sm:text-5xl lg:text-6xl mt-1">National Bill Board</h1>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-[#FDFBF7]/80 leading-relaxed">
            Every bill and vote F.E.R.Ms are watching across all 150 electorates. Ranked by how many members are watching and how fresh it is. Instrument beats inference — every entry points at its source.
          </p>
          <div className="mt-6 grid lg:grid-cols-[1fr_minmax(300px,420px)] gap-6 items-end">
            <div className="flex flex-wrap gap-6 text-sm">
              <span><strong className="font-type text-3xl text-[#E2705A]" data-testid="board-total">{data?.counts?.total ?? "–"}</strong> <span className="text-[#FDFBF7]/70">entries</span></span>
              <span><strong className="font-type text-3xl text-[#8FA58A]" data-testid="board-electorates">{data?.counts?.electorates ?? "–"}</strong> <span className="text-[#FDFBF7]/70">electorates contributing</span></span>
            </div>
            <WriteLetterCta />
          </div>
        </div>
        <div className="h-2 bg-[#E2705A]" />
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <BillSources intro="These official parliamentary pages update live as bills are introduced and progress. Open the one for your jurisdiction, read the instrument, then log what you find below — with the section numbers." />
        <div className="h-12" />
        <h2 className="font-type font-bold text-base md:text-lg text-[#1B2A41] uppercase tracking-wider mb-4">What F.E.R.Ms are watching</h2>
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="board-filters">
          <Chip active={f.sort === "hot"} onClick={() => setF({ ...f, sort: "hot" })} testId="board-sort-hot"><Flame className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />What's hot</Chip>
          <Chip active={f.sort === "new"} onClick={() => setF({ ...f, sort: "new" })} testId="board-sort-new"><Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Newest</Chip>
          <span className="w-px h-6 bg-[#D8D2C4] mx-1" />
          <select data-testid="board-filter-state" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className={sel}>
            <option value="">All states</option>
            {STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="board-filter-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={sel}>
            <option value="">Bills & votes</option>
            <option value="bill">Bills</option>
            <option value="vote">Votes</option>
          </select>
          <select data-testid="board-filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={sel}>
            <option value="">Any status</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-2 bg-[#FAF8F2] border border-[#D8D2C4] rounded-full px-3 h-8 ml-auto">
            <Search className="w-3.5 h-3.5 text-[#5C6A79]" />
            <input data-testid="board-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search" className="bg-transparent outline-none text-xs w-32 sm:w-48" />
          </div>
        </div>

        {isLoading ? <p className="text-sm text-[#5C6A79]">Loading…</p> : bills.length === 0 ? (
          <p data-testid="board-empty" className="text-sm text-[#5C6A79] rounded-2xl border-2 border-dashed border-[#D8D2C4] p-10 text-center">Nothing here yet. Sign in on your electorate page to log the first bill or vote.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="board-list">
            {bills.map((b) => <BillCard key={b.id} b={b} showElectorate showWatch />)}
          </div>
        )}
        {!member && (
          <p className="mt-8 text-xs text-[#5C6A79] text-center">F.E.R.Ms: open your electorate page and sign in with your registered email to log entries and mark what you're watching.</p>
        )}
      </main>
      <Footer />
    </div>
  );
}
